import { estateService } from "../services/estateService";
import { estateContext } from "../state/estateContext";
import { createOverlay } from "./overlay";
import { createStepController } from "./steps";
import { byId } from "./dom";
import type { Estate } from "../types";

export interface EstatesController {
  open(): void;
}

/** onEstateSwitched fires after the active estate actually changes (either
 *  by picking one from the list, or right after creating a new one) — the
 *  caller (appShell) uses it to refetch wallet/meters/bills/transactions
 *  for whichever estate is now active. */
export function initEstates(onEstateSwitched: () => void): EstatesController {
  const overlay = createOverlay("sheet-estates");
  const root = byId("estates-flow");
  const steps = createStepController(root);

  const listEl = byId("estate-list");
  const addBtn = byId<HTMLButtonElement>("estate-add-btn");

  const nameInput = byId<HTMLInputElement>("estate-add-name");
  const addressInput = byId<HTMLInputElement>("estate-add-address");
  const addError = byId("estate-add-error");
  const submitBtn = byId<HTMLButtonElement>("estate-add-submit-btn");
  const cancelBtn = byId("estate-add-cancel-btn");

  const successLine = byId("estate-success-line");
  const doneBtn = byId("estate-done-btn");

  const failureReason = byId("estate-failure-reason");
  const retryBtn = byId("estate-retry-btn");

  async function renderList(): Promise<void> {
    listEl.innerHTML = '<li class="bill-empty bill-empty--loading">Loading\u2026</li>';
    try {
      const estates = await estateService.listEstates();
      listEl.innerHTML = "";
      if (estates.length === 0) {
        listEl.innerHTML = '<li class="bill-empty">No estates yet.</li>';
        return;
      }
      estates.forEach((estate) => renderRow(estate));
    } catch (err) {
      listEl.innerHTML = "";
      const errLi = document.createElement("li");
      errLi.className = "bill-empty";
      errLi.textContent = err instanceof Error ? err.message : "Couldn't load estates.";
      listEl.appendChild(errLi);
    }
  }

  function renderRow(estate: Estate): void {
    const isActive = estate.id === estateContext.activeEstateId;
    const row = document.createElement("li");
    row.className = "estate-row";
    row.innerHTML = `
      <div>
        <div class="estate-row-name">${estate.name}</div>
        <div class="estate-row-address">${estate.address ?? "\u2014"}</div>
      </div>
      ${isActive ? '<span class="estate-row-badge">Viewing</span>' : ""}
    `;
    if (!isActive) {
      row.addEventListener("click", () => {
        estateContext.setActiveEstate(estate.id);
        overlay.close();
        onEstateSwitched();
      });
    }
    listEl.appendChild(row);
  }

  addBtn.addEventListener("click", () => {
    nameInput.value = "";
    addressInput.value = "";
    addError.hidden = true;
    steps.show("create");
    nameInput.focus();
  });

  cancelBtn.addEventListener("click", () => steps.show("list"));

  submitBtn.addEventListener("click", async () => {
    const name = nameInput.value.trim();
    if (!name) {
      addError.textContent = "Enter an estate name.";
      addError.hidden = false;
      return;
    }
    addError.hidden = true;
    steps.show("pending");

    try {
      const estate = await estateService.createEstate({ name, address: addressInput.value.trim() || undefined });
      estateContext.setActiveEstate(estate.id);
      onEstateSwitched();
      successLine.textContent = `${estate.name} created \u2014 you're now viewing it.`;
      steps.show("success");
    } catch (err) {
      failureReason.textContent = err instanceof Error ? err.message : "Couldn't create this estate.";
      steps.show("failed");
    }
  });

  retryBtn.addEventListener("click", () => steps.show("create"));
  doneBtn.addEventListener("click", () => {
    steps.show("list");
    overlay.close();
  });

  function open(): void {
    steps.show("list");
    void renderList();
    overlay.open();
  }

  return { open };
}
